import { Outlet, useNavigate } from "react-router-dom";
import { SidebarTrigger, SidebarInset } from "@/components/ui/sidebar"
import { AppSidebar } from "@/components/app-sidebar"
import Header from "../components/header"
import { CartSheet } from "modules/cart/components/cartSheet";
import { PageTitleProvider, usePageTitle } from "hooks/usePageTitle";
import { CartSheetProvider, useCartSheet } from "hooks/useCartSheet";
import { useCart } from "modules/cart/hooks/useCart";
import { ArrowLeft, MessageCircleQuestion } from "lucide-react";
import { PwaInstallBanner } from "./pwa-install-banner";
import { useState, useEffect, useRef } from "react";

export function HeaderBar() {
  const { title, showBack, headerAction, showCart, showHelp, titleAction, titleEditing, setTitleEditing, onTitleSave, hideHeaderTitle, breadcrumb } = usePageTitle();
  const { open, setOpen } = useCartSheet();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (titleEditing) {
      setEditValue(title);
    }
  }, [titleEditing, title]);

  const handleSave = () => {
    onTitleSave?.(editValue);
    setTitleEditing(false);
  };

  const handleCancel = () => {
    setEditValue(title);
    setTitleEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const handleBlur = () => {
    if (editValue !== title) {
      handleSave();
    } else {
      setTitleEditing(false);
    }
  };

  return (
    <header className="flex justify-between h-20 shrink-0 items-center gap-2  pl-4 pr-6 py-2">
      <div className="flex items-center gap-4 min-w-0">
        {showBack ? (
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-medium text-foreground">
            <ArrowLeft className="h-4 w-4" />
          </button>
        ) : (
          <SidebarTrigger className="-ml-1" />
        )}
        {titleEditing ? (
          <div className="flex items-center min-w-0 flex-1">
            <input
              ref={inputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              className="bg-transparent text-xl font-semibold outline-none w-full"
            />
          </div>
        ) : !hideHeaderTitle ? (
          <div className="flex items-center gap-3 min-w-0">
            {breadcrumb ?? (
              <>
                <h2 className="text-xl font-semibold truncate">{title}</h2>
                {titleAction}
              </>
            )}
          </div>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        {headerAction && <div>{headerAction}</div>}
        {showHelp && (
          <div>
            <a href="https://wa.me/56989326529" target="_blank" rel="noopener noreferrer" className="underline">
             <MessageCircleQuestion className="w-5 h-5"/>
            </a>
          </div>
        )}
        {(showCart || cart.length > 0) && <CartSheet open={open} setOpen={setOpen} />}
      </div>
    </header>
  );
}

export default function MainLayout() {
  return (
    <>
      <AppSidebar variant="inset" />
      <SidebarInset className="min-w-0">
        <PageTitleProvider>
          <CartSheetProvider>
            <HeaderBar />
            <Outlet />
          </CartSheetProvider>
        </PageTitleProvider>
      </SidebarInset>
      <PwaInstallBanner />
    </>
  )
}
